import { useState, useEffect } from 'react';
import {
  MapPin, Phone, Mail, Clock, Send, Check, X, ChevronDown,
  Instagram, Facebook, ArrowRight, HelpCircle, Truck, RotateCcw,
  Tag, Pencil, Save, Plus, Trash2, ExternalLink, Eye,
} from 'lucide-react';

// ── API helpers ───────────────────────────────────────────────────────────────
function apiUrl(path) {
  const base = (window.themeData?.apiUrl ?? '').replace(/\/$/, '');
  return `${base}/little-anvi/v1${path}`;
}
const authHdrs = () => ({
  'Content-Type': 'application/json',
  'X-WP-Nonce': window.themeData?.nonce ?? '',
});

// ── Default settings ──────────────────────────────────────────────────────────
const DEFAULTS = {
  badge: "We'd love to hear from you!",
  title: 'Get in Touch',
  subtitle: 'Have a question about an order, a product, or just want to say hi? Our friendly team is here to help!',
  address: '123 Play Street, Toyville\nCalifornia, CA 90210\nUnited States',
  phone: '+1 (555) 123-4567\n+1 (555) 987-6543\nMon–Fri, 9am–6pm PST',
  email: 'hello@kiddiejoy.com\nsupport@kiddiejoy.com\nWe reply within 24hrs',
  hours: 'Mon–Fri: 9:00am – 6:00pm\nSaturday: 10:00am – 4:00pm\nSunday: Closed',
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d423286.27405770525!2d-118.69192047471653!3d34.02016130653294!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c75ddc27da13%3A0xe22fefa1162c4f05!2sLos%20Angeles%2C%20CA!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus',
  mapAddress: '123 Play Street, Toyville, CA 90210',
  instagramUrl: '#',
  facebookUrl: '#',
  tiktokUrl: '#',
  faqUrl: '#',
  faqItems: ['How long does shipping take?', "What's your return policy?", 'How do I use a discount code?'],
  subjects: ['Order Tracking', 'Returns & Refunds', 'Product Inquiry', 'Wholesale / Bulk Orders', 'Partnership', 'Other'],
  recipientEmail: '',
};

// ── Info card config ──────────────────────────────────────────────────────────
const CARDS = [
  { key: 'address', label: 'Our Store',      Icon: MapPin, bg: 'bg-primary/10',  text: 'text-primary'  },
  { key: 'phone',   label: 'Phone',           Icon: Phone,  bg: 'bg-accent1/10', text: 'text-accent1'  },
  { key: 'email',   label: 'Email',           Icon: Mail,   bg: 'bg-accent2/10', text: 'text-accent2'  },
  { key: 'hours',   label: 'Working Hours',   Icon: Clock,  bg: 'bg-accent3/10', text: 'text-accent3'  },
];

// ── Admin Edit Drawer ─────────────────────────────────────────────────────────
function EditDrawer({ settings, onClose, onSave }) {
  const [form,    setForm]    = useState({ ...settings });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');
  const [tab,     setTab]     = useState('content');

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(apiUrl('/contact-settings'), {
        method:  'POST',
        headers: authHdrs(),
        body:    JSON.stringify({ settings: form }),
      });
      if (!res.ok) throw new Error();
      setMsg('Saved successfully!');
      onSave(form);
      setTimeout(() => setMsg(''), 2500);
    } catch { setMsg('Save failed. Please try again.'); }
    setSaving(false);
  }

  function addFaqItem() { setForm(f => ({ ...f, faqItems: [...(f.faqItems ?? []), ''] })); }
  function removeFaqItem(i) { setForm(f => ({ ...f, faqItems: f.faqItems.filter((_, idx) => idx !== i) })); }
  function editFaqItem(i, v) { setForm(f => { const a = [...(f.faqItems ?? [])]; a[i] = v; return { ...f, faqItems: a }; }); }

  function addSubject() { setForm(f => ({ ...f, subjects: [...(f.subjects ?? []), ''] })); }
  function removeSubject(i) { setForm(f => ({ ...f, subjects: f.subjects.filter((_, idx) => idx !== i) })); }
  function editSubject(i, v) { setForm(f => { const a = [...(f.subjects ?? [])]; a[i] = v; return { ...f, subjects: a }; }); }

  const TABS = [
    { id: 'content',  label: 'Content' },
    { id: 'map',      label: 'Map' },
    { id: 'social',   label: 'Social' },
    { id: 'faq',      label: 'FAQ' },
    { id: 'form',     label: 'Form' },
  ];

  const FieldText = ({ label, field, placeholder, multiline = false }) => (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {multiline
        ? <textarea value={form[field] ?? ''} onChange={e => set(field)(e.target.value)} rows={3} placeholder={placeholder}
            className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 resize-none" />
        : <input value={form[field] ?? ''} onChange={e => set(field)(e.target.value)} placeholder={placeholder}
            className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20" />
      }
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex">
      {/* Overlay */}
      <div className="flex-grow bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Drawer */}
      <div className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-primary/5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center"><Pencil className="w-4 h-4 text-white" /></div>
            <span className="font-black text-textDark">Edit Contact Page</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto hide-scrollbar">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-bold whitespace-nowrap transition-colors ${tab === t.id ? 'text-primary border-b-2 border-primary' : 'text-gray-400 hover:text-textDark'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto p-5 space-y-4">

          {tab === 'content' && (<>
            <FieldText label="Badge text"      field="badge"    placeholder="We'd love to hear from you!" />
            <FieldText label="Page title"      field="title"    placeholder="Get in Touch" />
            <FieldText label="Subtitle"        field="subtitle" placeholder="…" multiline />
            <FieldText label="Address (multi-line)" field="address" placeholder="123 Main St…" multiline />
            <FieldText label="Phone (multi-line)"   field="phone"   placeholder="+1 555 …" multiline />
            <FieldText label="Email (multi-line)"   field="email"   placeholder="hello@…" multiline />
            <FieldText label="Hours (multi-line)"   field="hours"   placeholder="Mon–Fri…" multiline />
          </>)}

          {tab === 'map' && (<>
            <FieldText label="Google Maps Embed URL" field="mapEmbedUrl" placeholder="https://www.google.com/maps/embed?pb=…" multiline />
            <p className="text-xs text-textLight">Get it from Google Maps → Share → Embed a map → Copy HTML → paste just the src="…" URL</p>
            <FieldText label="Address label (below map)" field="mapAddress" placeholder="123 Play Street…" />
            <FieldText label="Get Directions URL" field="mapDirectionsUrl" placeholder="https://maps.google.com/?q=…" />
          </>)}

          {tab === 'social' && (<>
            <FieldText label="Instagram URL" field="instagramUrl" placeholder="https://instagram.com/…" />
            <FieldText label="Facebook URL"  field="facebookUrl"  placeholder="https://facebook.com/…" />
            <FieldText label="TikTok URL"    field="tiktokUrl"    placeholder="https://tiktok.com/…" />
            <FieldText label="Section heading" field="socialHeading" placeholder="Follow Us for Fun! 🎨" />
          </>)}

          {tab === 'faq' && (<>
            <FieldText label="FAQ page URL" field="faqUrl" placeholder="/faq/" />
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">FAQ Items</label>
              <div className="space-y-2">
                {(form.faqItems ?? []).map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={item} onChange={e => editFaqItem(i, e.target.value)}
                      className="flex-grow text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary" />
                    <button onClick={() => removeFaqItem(i)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-red-50 text-red-400 transition-colors flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addFaqItem} className="mt-2 flex items-center gap-2 text-sm font-bold text-primary hover:text-primaryHover">
                <Plus className="w-4 h-4" /> Add item
              </button>
            </div>
            <FieldText label="FAQ CTA heading" field="faqHeading" placeholder="Need Quick Answers?" />
            <FieldText label="FAQ CTA subtext" field="faqSubtext" placeholder="Check our FAQ section…" />
          </>)}

          {tab === 'form' && (<>
            <FieldText label="Recipient email (leave blank for admin email)" field="recipientEmail" placeholder="contact@yourstore.com" />
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Subject Options</label>
              <div className="space-y-2">
                {(form.subjects ?? []).map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={s} onChange={e => editSubject(i, e.target.value)}
                      className="flex-grow text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary" />
                    <button onClick={() => removeSubject(i)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-red-50 text-red-400 transition-colors flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addSubject} className="mt-2 flex items-center gap-2 text-sm font-bold text-primary hover:text-primaryHover">
                <Plus className="w-4 h-4" /> Add subject
              </button>
            </div>
          </>)}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center gap-3">
          <button onClick={handleSave} disabled={saving}
            className="flex-grow bg-primary hover:bg-primaryHover text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? 'Saving…' : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
          {msg && <span className={`text-xs font-bold ${msg.includes('!') ? 'text-accent3' : 'text-red-500'}`}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Contact form ──────────────────────────────────────────────────────────────
function ContactForm({ settings }) {
  const [form,    setForm]    = useState({ firstName: '', lastName: '', email: '', phone: '', subject: '', orderNumber: '', message: '', agree: false });
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [err,     setErr]     = useState('');

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.agree) { setErr('Please agree to the privacy policy.'); return; }
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(apiUrl('/contact/submit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': window.themeData?.nonce ?? '' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setSent(true);
    } catch (ex) { setErr(ex.message || 'Failed to send. Please try again.'); }
    setLoading(false);
  }

  if (sent) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-accent3/10 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-accent3/20">
          <Check className="w-8 h-8 text-accent3" />
        </div>
        <h3 className="text-2xl font-black text-textDark mb-2">Message Sent! 🎉</h3>
        <p className="text-textLight font-bold">Thanks for reaching out! We'll get back to you within 24 hours.</p>
        <button onClick={() => { setSent(false); setForm({ firstName:'',lastName:'',email:'',phone:'',subject:'',orderNumber:'',message:'',agree:false }); }}
          className="mt-6 px-6 py-3 bg-primary/10 text-primary rounded-xl font-bold hover:bg-primary/20 transition-colors">
          Send Another Message
        </button>
      </div>
    );
  }

  const subjects = settings.subjects ?? DEFAULTS.subjects;

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[['First Name','firstName','e.g. Jane',true],['Last Name','lastName','e.g. Doe',true]].map(([lbl,k,ph,req]) => (
          <div key={k}>
            <label className="block text-sm font-black text-textDark mb-2">{lbl}{req && <span className="text-accent1 ml-1">*</span>}</label>
            <input type="text" value={form[k]} onChange={e => set(k)(e.target.value)} placeholder={ph} required={req}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-black text-textDark mb-2">Email Address <span className="text-accent1">*</span></label>
          <input type="email" value={form.email} onChange={e => set('email')(e.target.value)} placeholder="jane@example.com" required
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
        </div>
        <div>
          <label className="block text-sm font-black text-textDark mb-2">Phone Number</label>
          <input type="tel" value={form.phone} onChange={e => set('phone')(e.target.value)} placeholder="+1 (555) 000-0000"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-black text-textDark mb-2">Subject <span className="text-accent1">*</span></label>
        <div className="relative">
          <select value={form.subject} onChange={e => set('subject')(e.target.value)} required
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all appearance-none cursor-pointer">
            <option value="" disabled>Select a topic…</option>
            {subjects.map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-black text-textDark mb-2">Order Number <span className="text-textLight font-bold">(optional)</span></label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-textLight font-black text-sm">#</span>
          <input type="text" value={form.orderNumber} onChange={e => set('orderNumber')(e.target.value)} placeholder="KID-84729"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-8 pr-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-black text-textDark mb-2">Your Message <span className="text-accent1">*</span></label>
        <textarea value={form.message} onChange={e => set('message')(e.target.value)} rows={5} placeholder="Tell us how we can help you today… 😊" required
          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none" />
      </div>
      <div className="flex items-start gap-3">
        <input type="checkbox" id="agree" checked={form.agree} onChange={e => set('agree')(e.target.checked)}
          className="mt-1 w-4 h-4 accent-primary cursor-pointer flex-shrink-0" />
        <label htmlFor="agree" className="text-sm text-textLight font-bold cursor-pointer">
          I agree to the <a href="#" className="text-primary hover:underline">Privacy Policy</a> and consent to being contacted.
        </label>
      </div>
      {err && <p className="text-red-500 text-sm font-bold">{err}</p>}
      <button type="submit" disabled={loading}
        className="w-full py-4 bg-primary text-white rounded-2xl font-black text-base hover:bg-primaryHover transition-all shadow-soft hover:shadow-float hover:-translate-y-0.5 flex items-center justify-center gap-3 disabled:opacity-60">
        {loading ? 'Sending…' : <><Send className="w-5 h-5" /> Send Message</>}
      </button>
    </form>
  );
}

// ── Main ContactPage component ────────────────────────────────────────────────
// Props arrive in two ways:
//   editableProps={…}  — passed from builder live-preview
//   badge=… title=…    — spread directly when rendered as a section-block on the frontend
export default function ContactPage({ editableProps, badge, title, ...rest } = {}) {
  // Treat either form as "coming from the builder/layout"
  const builderProps = editableProps ?? (badge !== undefined || title !== undefined
    ? { badge, title, ...rest }
    : null);
  const fromBuilder = !!builderProps;

  const [settings, setSettings] = useState(builderProps ?? DEFAULTS);
  const [loading,  setLoading]  = useState(!fromBuilder);
  const [editOpen, setEditOpen] = useState(false);

  // Show admin edit button only on the live frontend (not in builder preview / section-block render).
  const isAdmin = !fromBuilder && !!(window.themeData?.isAdmin ?? (window.themeData?.isLoggedIn && window.wpApiSettings));

  useEffect(() => {
    if (fromBuilder) return; // props came from builder, skip REST fetch
    fetch(apiUrl('/contact-info'))
      .then(r => r.json())
      .then(data => setSettings({ ...DEFAULTS, ...data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = settings;

  const faqItems = s.faqItems ?? DEFAULTS.faqItems;
  const siteUrl  = window.themeData?.siteUrl ?? '';

  return (
    <div className="pb-16">

      {/* ── Hero Banner ── */}
      {!s.hideHero && (
      <section className="w-full bg-gradient-to-br from-primary/10 via-accent1/10 to-secondary/20 py-16 md:py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent1/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl pointer-events-none" />
        <div className="absolute top-8 right-16 opacity-20 hidden lg:block animate-bounce">
          <span className="text-5xl text-secondary">★</span>
        </div>
        <div className="absolute bottom-8 left-24 opacity-20 hidden lg:block">
          <span className="text-4xl text-accent1">♥</span>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2 shadow-soft mb-6 border border-primary/10">
            <Mail className="w-4 h-4 text-primary" />
            <span className="text-primary font-bold text-sm">{s.badge || DEFAULTS.badge}</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-textDark italic mb-4">
            {s.title?.split(' ').slice(0,-1).join(' ') || 'Get in'}{' '}
            <span className="text-primary">{s.title?.split(' ').slice(-1)[0] || 'Touch'}</span>{' '}
            <span className="text-secondary">🎉</span>
          </h1>
          <p className="text-textLight font-bold text-base md:text-lg max-w-xl mx-auto">{s.subtitle || DEFAULTS.subtitle}</p>
        </div>
      </section>
      )}

      {/* ── Info Cards ── */}
      {!s.hideCards && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {CARDS.map(({ key, label, Icon, bg, text }) => (
            <div key={key} className="bg-white rounded-[24px] p-6 shadow-card border border-gray-100 flex flex-col items-center text-center hover:shadow-soft hover:-translate-y-1 transition-all duration-300">
              <div className={`w-14 h-14 ${bg} rounded-2xl flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${text}`} />
              </div>
              <h4 className="font-black text-textDark text-base mb-1">{label}</h4>
              <p className="text-sm text-textLight font-bold leading-relaxed whitespace-pre-line">{s[key] || DEFAULTS[key]}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── Form + Right Column ── */}
      {(!s.hideForm || !s.hideMap || !s.hideFaq || !s.hideSocial) && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 md:mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* Contact Form */}
          {!s.hideForm && (
          <div className={!s.hideMap && !s.hideFaq && !s.hideSocial ? 'lg:col-span-3' : 'lg:col-span-5'}>
            <div className="bg-white rounded-[32px] p-8 md:p-10 shadow-sm border border-gray-100">
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-black text-textDark italic mb-2">Send Us a Message <span className="text-primary">✉️</span></h2>
                <p className="text-textLight font-bold text-sm">Fill out the form below and we'll get back to you as soon as possible.</p>
              </div>
              <ContactForm settings={s} />
            </div>
          </div>
          )}

          {/* Right Column */}
          {(!s.hideMap || !s.hideFaq || !s.hideSocial) && (
          <div className={!s.hideForm ? 'lg:col-span-2 space-y-6' : 'lg:col-span-5 grid grid-cols-1 md:grid-cols-3 gap-6'}>

            {/* Map */}
            {!s.hideMap && (
            <div className="bg-white rounded-[32px] p-4 shadow-sm border border-gray-100 overflow-hidden">
              <div className="h-72 md:h-80 relative rounded-[20px] overflow-hidden">
                <iframe
                  src={s.mapEmbedUrl || DEFAULTS.mapEmbedUrl}
                  width="100%" height="100%"
                  style={{ border: 0, borderRadius: '1.25rem' }}
                  allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Store location"
                />
              </div>
              <div className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-grow min-w-0">
                  <p className="font-black text-textDark text-sm">Store Location</p>
                  <p className="text-xs text-textLight font-bold truncate">{s.mapAddress || DEFAULTS.mapAddress}</p>
                </div>
                <a href={s.mapDirectionsUrl || `https://maps.google.com/?q=${encodeURIComponent(s.mapAddress || DEFAULTS.mapAddress)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs font-black text-primary hover:text-primaryHover bg-primary/10 px-3 py-2 rounded-xl hover:bg-primary/20 transition-colors whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                  Get Directions <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            )}

            {/* FAQ Teaser */}
            {!s.hideFaq && (
            <div className="bg-gradient-to-br from-primary to-primaryHover rounded-[28px] p-6 md:p-8 text-white shadow-soft">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-black text-lg mb-1">{s.faqHeading || 'Need Quick Answers?'}</h4>
                  <p className="text-white/80 text-sm font-bold">{s.faqSubtext || 'Check our FAQ section for instant answers to common questions.'}</p>
                </div>
              </div>
              <div className="space-y-2 mb-5">
                {faqItems.slice(0, 3).map((item, i) => {
                  const icons = [<Truck key="t" className="w-3 h-3 text-secondary" />, <RotateCcw key="r" className="w-3 h-3 text-secondary" />, <Tag key="tg" className="w-3 h-3 text-secondary" />];
                  return (
                    <div key={i} className="bg-white/10 rounded-xl px-4 py-3 text-sm font-bold flex items-center gap-2 cursor-pointer hover:bg-white/20 transition-colors">
                      {icons[i % icons.length]} {item}
                    </div>
                  );
                })}
              </div>
              <a href={s.faqUrl || '#'} className="inline-flex items-center gap-2 bg-white text-primary font-black text-sm px-5 py-3 rounded-xl hover:bg-secondary transition-colors hover:text-textDark">
                View All FAQs <ArrowRight className="w-3 h-3" />
              </a>
            </div>
            )}

            {/* Social Media */}
            {!s.hideSocial && (
            <div className="hidden bg-white rounded-[28px] p-6 shadow-sm border border-gray-100">
              <h4 className="font-black text-textDark text-base mb-4 italic">{s.socialHeading || 'Follow Us for Fun! 🎨'}</h4>
              <div className="grid grid-cols-3 gap-3">
                <a href={s.instagramUrl || '#'} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:scale-105 transition-transform">
                  <Instagram className="w-6 h-6" />
                  <span className="text-xs font-black">Instagram</span>
                </a>
                <a href={s.facebookUrl || '#'} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white hover:scale-105 transition-transform">
                  <Facebook className="w-6 h-6" />
                  <span className="text-xs font-black">Facebook</span>
                </a>
                <a href={s.tiktokUrl || '#'} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 text-white hover:scale-105 transition-transform">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 448 512"><path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/></svg>
                  <span className="text-xs font-black">TikTok</span>
                </a>
              </div>
            </div>
            )}
          </div>
          )}
        </div>
      </section>
      )}

      {/* ── Admin Edit Button (only for admins) ── */}
      {isAdmin && (
        <button
          onClick={() => setEditOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-primary hover:bg-primaryHover text-white font-black px-5 py-3 rounded-2xl shadow-soft transition-all hover:shadow-float hover:-translate-y-0.5"
          title="Edit contact page content"
        >
          <Pencil className="w-4 h-4" /> Edit Page Info
        </button>
      )}

      {editOpen && (
        <EditDrawer
          settings={s}
          onClose={() => setEditOpen(false)}
          onSave={updated => { setSettings(updated); setEditOpen(false); }}
        />
      )}
    </div>
  );
}
